// ─── Embedded Data Applet settings ──────────────────────────────────────────
//
// Modal-only settings section that shows the two iframe snippets a
// site owner can paste into an external page + a tree picker of
// sites/devices/sensors/parameters to control what the applet
// renders. A live preview mirrors what the applet will look like.
//
// Config persisted to localStorage keyed by (projectId) until the
// backend endpoint lands.
import { useEffect, useMemo, useState } from 'react'
import { X, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react'
import { projectsApi } from '../../../lib/projectsApi'

const DEFAULT_CONFIG = {
  allow_embed:  true,
  parameter_ids: [],   // flat list — expansion is UI-only
}

function storageKey(projectId) {
  return `wqdata.embeddedApplet.${projectId || 'anon'}`
}

function loadConfig(projectId) {
  try {
    const raw = localStorage.getItem(storageKey(projectId))
    if (raw) return { ...DEFAULT_CONFIG, ...(JSON.parse(raw) || {}) }
  } catch { /* ignore malformed cache */ }
  return { ...DEFAULT_CONFIG }
}

function saveConfig(projectId, cfg) {
  try { localStorage.setItem(storageKey(projectId), JSON.stringify(cfg)) } catch {}
}

export default function EmbeddedDataAppletPanel({
  projectId,
  projectName = '',
  sites = [],
  onClose,
  readOnly = false,
}) {
  // Seed instantly from the localStorage cache so the modal paints
  // without a spinner, then reconcile with the server (the source of
  // truth the public embed reads). `ready` flips once the fetch
  // settles; it keys the modal so it re-seeds its internal state from
  // the authoritative config.
  const [config, setConfig] = useState(() => loadConfig(projectId))
  const [ready,  setReady]  = useState(false)

  useEffect(() => {
    let cancelled = false
    setReady(false)
    setConfig(loadConfig(projectId))
    projectsApi.getAppletConfig(projectId)
      .then((res) => {
        if (cancelled || !res) return
        const next = {
          allow_embed:   !!res.allow_embed,
          parameter_ids: Array.isArray(res.parameter_ids) ? res.parameter_ids : [],
        }
        setConfig(next)
        saveConfig(projectId, next)   // refresh the cache
      })
      .catch(() => { /* keep the localStorage seed on failure */ })
      .finally(() => { if (!cancelled) setReady(true) })
    return () => { cancelled = true }
  }, [projectId])

  return (
    <EmbeddedDataAppletModal
      key={`${projectId}-${ready}`}
      projectId={projectId}
      projectName={projectName}
      sites={sites}
      initial={config}
      readOnly={readOnly}
      onCancel={() => { if (onClose) onClose() }}
      // Persist to the server; the localStorage cache is updated too so
      // a reopen paints the latest pick instantly. Throws on failure so
      // the modal can keep itself open and surface an error.
      onSave={async (next) => {
        await projectsApi.saveAppletConfig(projectId, next)
        setConfig(next)
        saveConfig(projectId, next)
      }}
    />
  )
}

// ─── The modal ──────────────────────────────────────────────────────────────
function EmbeddedDataAppletModal({ projectId, projectName, sites, initial, onCancel, onSave, readOnly = false }) {
  const [allowEmbed, setAllowEmbed] = useState(!!initial.allow_embed)
  const [paramIds, setParamIds]     = useState(new Set(initial.parameter_ids || []))
  const [copied, setCopied]         = useState('')
  const [expanded, setExpanded]     = useState({}) // { key: bool }
  const [saving, setSaving]         = useState(false)
  const [saveError, setSaveError]   = useState('')

  // Use the current site's origin so stage builds embed the stage URL,
  // production embeds the production URL, etc. Falls back to the
  // legacy prod host during SSR / non-browser contexts.
  const origin = (typeof window !== 'undefined' && window.location?.origin) || 'https://www.wqdatalive.com'
  const embedBase = `${origin}/project/applet`
  const htmlIframe = `<iframe src="${embedBase}/html/${projectId || ''}?refresh=true" allowtransparency=true style="width: 300px; height: 420px; overflow-y: hidden; overflow-x: hidden;"></iframe>`
  const textIframe = `<iframe src="${embedBase}/text/${projectId || ''}?refresh=true" allowtransparency=true style="width: 300px; height: 420px; overflow-y: hidden; overflow-x: hidden;"></iframe>`

  const toggleExpand = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
  const isExpanded   = (key) => !!expanded[key]

  const toggleParam = (id) => {
    setParamIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  // Toggle a container (site / device / sensor): flip every parameter
  // under it based on the majority state — if any are off, turn them
  // all on; else turn them all off. Matches the mockup's tri-state.
  const containerParamIds = (site, device, sensor) => {
    const out = []
    ;(site?.devices || []).forEach((d) => {
      if (device && d !== device) return
      ;(d.sensors || []).forEach((s) => {
        if (sensor && s !== sensor) return
        ;(s.parameters || []).forEach((p) => out.push(p.id))
      })
    })
    return out
  }
  const containerState = (ids) => {
    const total = ids.length
    if (!total) return 'none'
    const on = ids.filter((id) => paramIds.has(id)).length
    if (on === 0)     return 'none'
    if (on === total) return 'all'
    return 'some'
  }
  const toggleContainer = (ids) => {
    const state = containerState(ids)
    setParamIds((prev) => {
      const next = new Set(prev)
      if (state === 'all') ids.forEach((id) => next.delete(id))
      else                 ids.forEach((id) => next.add(id))
      return next
    })
  }

  const doCopy = (text, key) => {
    try {
      navigator.clipboard.writeText(text)
      setCopied(key)
      setTimeout(() => setCopied(''), 1500)
    } catch {}
  }

  const submit = async () => {
    setSaving(true)
    setSaveError('')
    try {
      await onSave({
        allow_embed:   allowEmbed,
        parameter_ids: Array.from(paramIds),
      })
      onCancel()   // close only after the save succeeds
    } catch {
      setSaveError('Could not save. Check your connection and try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-4 pt-6" onClick={onCancel}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col mt-8"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Embedded Data Applet</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          {/* Read-only outside Edit Mode: banner + disabled fieldset inside the
              modal (see PublicPortalPanel for why it's not an EditModeGate wrap). */}
          {readOnly && (
            <div className="mb-4 px-4 py-2.5 bg-[#F1F2F4] border border-gray-200 rounded text-sm text-[#4D4D4D]">
              Embedded Data Applet settings are read-only. Choose <strong>Settings &rarr; Edit Mode</strong> to make changes.
            </div>
          )}
          <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* ── Left column (2/3): snippets + tree ─────────────── */}
          <div className="md:col-span-2 space-y-4">
            <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-3">Set Up Embedded Data Applet</h3>

            <div>
              <p className="common-size15 font-semibold text-xs text-[#4D4D4D] mb-3">
                Copy and paste the following HTML code into your website to display a <strong>stylized</strong> applet on one of your pages.
              </p>
              <div className="relative">
                <textarea
                  readOnly
                  value={htmlIframe}
                  className="w-full font-mono common-size15 text-[#888888] px-3 py-2 pr-10 border border-gray-200 rounded-md bg-gray-50 resize-none"
                  rows={3}
                />
                <button
                  type="button"
                  onClick={() => doCopy(htmlIframe, 'html')}
                  title="Copy to clipboard"
                  className="absolute top-1 right-1 p-1.5 text-gray-500 hover:text-[#358ECD] hover:bg-white rounded"
                >
                  {copied === 'html' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <p className="common-size15 font-semibold text-xs text-[#4D4D4D] mb-3">
                Copy and paste the following HTML code into your website to display a <strong>plain text</strong> applet on one of your pages.
              </p>
              <div className="relative">
                <textarea
                  readOnly
                  value={textIframe}
                  className="w-full font-mono common-size15 text-[#888888] px-3 py-2 pr-10 border border-gray-200 rounded-md bg-gray-50 resize-none"
                  rows={3}
                />
                <button
                  type="button"
                  onClick={() => doCopy(textIframe, 'text')}
                  title="Copy to clipboard"
                  className="absolute top-1 right-1 p-1.5 text-gray-500 hover:text-[#358ECD] hover:bg-white rounded"
                >
                  {copied === 'text' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm common-size18 font-semibold text-xs text-[#4D4D4D] pt-2 mb-4">
              <input
                type="checkbox"
                checked={allowEmbed}
                onChange={(e) => setAllowEmbed(e.target.checked)}
                className="rounded accent-clr"
              />
              Allow applet to be embedded in another site
            </label>

            <div className="pt-1">
              <h4 className="common-size18 font-bold text-xs text-[#4D4D4D] font-bold text-gray-800 mb-4">Select Sites and Parameters to Display</h4>
              {sites.length === 0 ? (
                <p className="text-xs common-size15 text-gray-500 italic">No sites in this project yet.</p>
              ) : (
                <div className="space-y-4 text-sm">
                  {sites.map((site) => {
                    const siteIds = containerParamIds(site)
                    const siteState = containerState(siteIds)
                    const siteKey = `site-${site.id}`
                    return (
                      <div key={site.id}>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => toggleExpand(siteKey)}
                                  className="text-gray-500 hover:text-gray-700 p-0.5">
                            {isExpanded(siteKey) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                          <TriStateBox state={siteState} onChange={() => toggleContainer(siteIds)} />
                          <span className="text-gray-800">{site.name}</span>
                        </div>
                        {isExpanded(siteKey) && (site.devices || []).map((dev) => {
                          const devIds = containerParamIds(site, dev)
                          const devState = containerState(devIds)
                          const devKey = `dev-${site.id}-${dev.id}`
                          return (
                            <div key={dev.id} className="ml-5">
                              <div className="flex items-center gap-1">
                                <button type="button" onClick={() => toggleExpand(devKey)}
                                        className="text-gray-500 hover:text-gray-700 p-0.5">
                                  {isExpanded(devKey) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </button>
                                <TriStateBox state={devState} onChange={() => toggleContainer(devIds)} />
                                <span className="text-gray-800">{dev.name || `Device #${dev.id}`}</span>
                              </div>
                              {isExpanded(devKey) && (dev.sensors || []).map((sensor) => {
                                const sensorIds = containerParamIds(site, dev, sensor)
                                const sensorState = containerState(sensorIds)
                                const sensorKey = `sensor-${site.id}-${dev.id}-${sensor.id}`
                                return (
                                  <div key={sensor.id} className="ml-5">
                                    <div className="flex items-center gap-1">
                                      <button type="button" onClick={() => toggleExpand(sensorKey)}
                                              className="text-gray-500 hover:text-gray-700 p-0.5">
                                        {isExpanded(sensorKey) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                      </button>
                                      <TriStateBox state={sensorState} onChange={() => toggleContainer(sensorIds)} />
                                      <span className="text-gray-800">{sensor.name || `Sensor #${sensor.id}`}</span>
                                    </div>
                                    {isExpanded(sensorKey) && (sensor.parameters || []).map((p) => (
                                      <label key={p.id} className="ml-10 flex items-center gap-2 py-0.5">
                                        <input
                                          type="checkbox"
                                          checked={paramIds.has(p.id)}
                                          onChange={() => toggleParam(p.id)}
                                          className="rounded accent-clr"
                                        />
                                        <span className="text-gray-700 text-sm">
                                          {p.name}{p.unit ? ` (${p.unit})` : ''}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Right column (1/3): live preview ───────────────── */}
          <div className="md:col-span-1">
            <AppletPreview projectName={projectName} sites={sites} paramIds={paramIds} />
          </div>
          </fieldset>
        </div>

        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-end gap-4">
          {saveError && <span className="text-sm text-red-600 mr-auto">{saveError}</span>}
          <button type="button" onClick={onCancel} disabled={saving}
                  className="text-sm text-[#358ECD] hover:underline disabled:opacity-50">{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && (
            <button type="button" onClick={submit} disabled={saving}
                    className="px-5 py-1.5 bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm font-semibold rounded disabled:opacity-60">
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// A checkbox that shows three states: unchecked, partial (some
// children checked), or fully checked. Uses `indeterminate` on the
// underlying input so screen-readers report the tri-state.
function TriStateBox({ state, onChange }) {
  return (
    <input
      type="checkbox"
      checked={state === 'all'}
      ref={(el) => { if (el) el.indeterminate = (state === 'some') }}
      onChange={onChange}
      className="rounded accent-clr"
    />
  )
}

// ─── Preview card ───────────────────────────────────────────────────────────
function AppletPreview({ projectName, sites, paramIds }) {
  // Group the selected parameters back into a flat list with their
  // context (site name), then bucket by "latest reading timestamp"
  // so the preview mimics the real applet's layout. Timestamps and
  // values are static placeholders — the runtime applet will read
  // them live from the data endpoints.
  const rows = useMemo(() => {
    const out = []
    for (const site of sites) {
      for (const dev of (site.devices || [])) {
        for (const sensor of (dev.sensors || [])) {
          for (const p of (sensor.parameters || [])) {
            if (paramIds.has(p.id)) {
              out.push({ id: p.id, name: p.name, unit: p.unit || '', site: site.name })
            }
          }
        }
      }
    }
    return out
  }, [sites, paramIds])

  // Fake timestamps + values just for the preview so users can tell
  // the applet renders a list of {name, value, unit} groupings — real
  // data comes from the live endpoint at runtime.
  const preview = useMemo(() => {
    const now = '07-17-2026 20:00:15'
    const prev = '07-17-2026 20:00:00'
    if (rows.length === 0) {
      return [
        { ts: now, items: [
          { name: 'Cell Module Power',     value: '-100100.0', unit: 'V' },
          { name: 'Cell Module Current',   value: '76.2',      unit: 'mA' },
          { name: 'Cell Signal Strength',  value: '-55',       unit: 'dB' },
          { name: 'Cell Status',           value: '0',         unit: '' },
        ]},
      ]
    }
    // Split into two blocks so the preview matches the mockup's shape.
    const half = Math.ceil(rows.length / 2)
    return [
      { ts: now,  items: rows.slice(0, half).map((r, i) => ({ name: r.name, value: (100 + i * 7).toFixed(1), unit: r.unit })) },
      { ts: prev, items: rows.slice(half).map((r, i) => ({ name: r.name, value: (55 + i * 3).toFixed(1),   unit: r.unit })) },
    ].filter((b) => b.items.length > 0)
  }, [rows])

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-4 border-b border-gray-100">
        <h4 className="common-size18 text-sm font-bold text-[#4D4D4D] font-semibold text-gray-800 truncate" title={projectName}>
          {projectName || 'Environmental Field Station'}
        </h4>
      </div>
      <div className="px-4 py-4 space-y-4 text-sm">
        {preview.map((block, idx) => (
          <div key={idx}>
            <div className="common-size15 text-xs text-[#888888] text-center mb-3">At {block.ts}</div>
            <div className="space-y-1">
              {block.items.map((it, j) => (
                <div key={j} className="flex items-center justify-between border-gray-100 py-0.5">
                  <span className="common-size15 text-sm text-[#4D4D4D] truncate mr-2">{it.name}</span>
                  <span className="common-size15 text-sm text-[#4D4D4D] truncate mr-2 whitespace-nowrap">
                    {it.value}{it.unit ? ` ${it.unit}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 py-4 text-center common-size15 text-[#888888] border-t bg-gray-50">
        Powered By <a href="https://www.wqdatalive.com" target="_blank" rel="noreferrer" className="text-[#358ECD] hover:underline">WQData LIVE</a>
      </div>
    </div>
  )
}
